import { Service } from "typedi";
import { v4 as uuidv4 } from "uuid";
import {
    BrowserTabDTO,
    BrowserWindowDTO,
    DesktopSnapshotDTO
} from "../../../../../schools-backend/src/student/application/dtos/DesktopSnapshot";
import { InstanceCoordinator, InstanceBroadcastMessage } from "../InstanceCoordinator";
import { ContentScriptRegistry, TabState } from "../ContentScriptRegistry";

const SNAPSHOT_TIMEOUT_MS = 1500;

interface TabReport extends TabState {}

/**
 * Builds a single `DesktopSnapshotDTO` by asking every controller instance
 * for the state of its content script. Only meaningful on the leader.
 *
 * The outcome has exactly one `BrowserWindowDTO` — the browser itself — and
 * one `BrowserTabDTO` per live content script in the browser.
 */
@Service()
export class DesktopSnapshotAggregator {
    private sessionId: string = uuidv4();

    constructor(
        private readonly coordinator: InstanceCoordinator,
        private readonly localRegistry: ContentScriptRegistry
    ) {}

    setSessionId(id: string): void {
        this.sessionId = id;
    }

    async collect(): Promise<DesktopSnapshotDTO> {
        const requestId = uuidv4();
        const reports: TabReport[] = [];

        const local = this.localRegistry.getState();
        if (local) reports.push(local);

        const unsubscribe = this.coordinator.onMessage((msg: InstanceBroadcastMessage) => {
            if (msg.kind !== "snapshot-report" || msg.requestId !== requestId) return;
            reports.push({
                tabId: msg.tabId,
                url: msg.url,
                title: msg.title,
                favicon: msg.favicon,
                active: msg.active
            });
        });

        this.coordinator.broadcast({ kind: "request-snapshot-report", requestId });

        await new Promise((resolve) => setTimeout(resolve, SNAPSHOT_TIMEOUT_MS));
        unsubscribe();

        const tabs: BrowserTabDTO[] = reports.map((r, index) => ({
            id: index + 1,
            active: r.active,
            title: r.title || null,
            url: r.url || null,
            faviconUrl: r.favicon ?? null,
            screenshot: null
        }));

        const window: BrowserWindowDTO = {
            type: "browser",
            id: 1,
            focused: true,
            incognito: false,
            screenshot: null,
            tabs
        };

        return {
            sessionId: this.sessionId,
            windows: [window]
        };
    }
}
